import React, { useEffect, useRef } from 'react';
import { Box, Checkbox, FormControlLabel, Link, Stack, Typography } from '@mui/material';
import { CampaignSummary } from './CampaignSummary';
import { useCampaignForm, useIsMobile } from 'hooks';
import { CampaignFields, CampaignStep, RouteList } from 'types';
import { AddressForm, type AddressFormProps } from 'components/AddressForm';
import { InfoMessage } from 'components/InfoMessage';
import { campaignBillingAddressFormConfig, campaignContactAddressFormConfig } from 'shared';

export const Complete = () => {
  const {
    formState: { errors },
    control,
    onFieldChange,
    getFieldControls,
    watch,
    setValue,
    hasCreativesWarning,
    hasCreativesError,
    hasInThePastError,
    redirectToStep,
  } = useCampaignForm();
  const errorRef = useRef<HTMLInputElement | null>(null);
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (hasInThePastError || hasCreativesError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [hasCreativesError, hasInThePastError]);

  const billingAddressEqualToContact = watch(CampaignFields.billingAddressEqualToContact);

  // Watch for changes to the billingAddressEqualToContact checkbox
  // When checked, copy contact address to billing address
  useEffect(() => {
    if (billingAddressEqualToContact) {
      // Get current contact address values
      const contactValues = watch();

      // Copy contact address to billing address
      setValue(CampaignFields.billingAddressSalutation, contactValues.contactAddressSalutation);
      setValue(CampaignFields.billingAddressFirstName, contactValues.contactAddressFirstName);
      setValue(CampaignFields.billingAddressLastName, contactValues.contactAddressLastName);
      setValue(CampaignFields.billingAddressLine1, contactValues.contactAddressLine1);
      setValue(CampaignFields.billingAddressLine2, contactValues.contactAddressLine2);
      setValue(CampaignFields.billingAddressCountry, contactValues.contactAddressCountry);
      setValue(CampaignFields.billingAddressPostcode, contactValues.contactAddressPostcode);
      setValue(CampaignFields.billingAddressCity, contactValues.contactAddressCity);
      setValue(CampaignFields.billingAddressEmail, contactValues.contactAddressEmail);
      setValue(CampaignFields.billingAddressPhone, contactValues.contactAddressPhone);
      setValue(CampaignFields.billingAddressCompanyName, contactValues.contactAddressCompanyName);
    }
  }, [billingAddressEqualToContact, setValue, watch]);

  const renderAddressForm = (isBilling: boolean) => {
    const addressFormProps = {
      control,
      errors,
      testId: isBilling ? 'billing-address-form' : 'contact-address-form',
      formConfig: isBilling ? campaignBillingAddressFormConfig : campaignContactAddressFormConfig,
      onFieldChange,
      // need to loosen types to adjust useForm members
    } as AddressFormProps<any, any>;

    return <AddressForm {...addressFormProps} />;
  };

  return (
    <Stack gap={10}>
      <Stack gap={6} ref={errorRef}>
        {hasCreativesWarning && (
          <InfoMessage
            fullWidth
            variant={hasCreativesError ? 'error' : 'warning'}
            title="Fehlende Creatives"
            message={
              <Typography>
                Um die Bestellung abzuschliessen, lade bitte zuerst die fehlenden Creatives hoch.
                <br />
                <Link
                  href={'#'}
                  color={'text.primary'}
                  onClick={(e) => {
                    e.preventDefault();
                    redirectToStep(CampaignStep.Creatives);
                  }}
                >
                  Creatives hochladen
                </Link>
              </Typography>
            }
          />
        )}
        {hasInThePastError && (
          <InfoMessage
            fullWidth
            variant="error"
            title="Kampagnendatum überprüfen"
            message={
              <Typography>
                Um die Bestellung abzuschliessen, passe bitte das Datum der Kampagne an.
                <br />
                <Link
                  href={'#'}
                  color={'text.primary'}
                  onClick={(e) => {
                    e.preventDefault();
                    redirectToStep(CampaignStep.Basic);
                  }}
                >
                  Datum anpassen
                </Link>
              </Typography>
            }
          />
        )}
        <CampaignSummary />
        <Stack sx={{ mt: 10 }}>
          <Typography variant="h3" mb={6} sx={({ spacing }) => ({ lineHeight: spacing(7) })}>
            Firmenadresse und Ansprechperson der Kampagne
          </Typography>
          {renderAddressForm(false)}
        </Stack>
        <Stack sx={{ mt: 10 }} spacing={6}>
          <Typography variant="h3">Rechnungsadresse</Typography>
          <Box>
            <Typography variant="h5">Die Abrechnung erfolgt monatlich per Rechnung via E-Mail.</Typography>
            <Typography variant="body2">
              Weitere Bezahlmöglichkeiten werden zu einem späteren Zeitpunkt folgen.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                sx={{ pl: 0 }}
                disableRipple
                checked={billingAddressEqualToContact}
                {...getFieldControls(CampaignFields.billingAddressEqualToContact)}
              />
            }
            style={{ marginRight: isMobile ? 0 : '30%' }}
            label="Rechnungsadresse entspricht der Firmenadresse"
          />
          {!billingAddressEqualToContact && renderAddressForm(true)}
          <Typography sx={{ pr: isMobile ? 0 : '30%' }} variant="body2">
            Mit Klick auf «Kostenpflichtig bestellen» bin ich mit den{' '}
            <Link title="AGB" href={RouteList.TermsAndConditions} sx={{ color: 'grey.800' }} target="_blank">
              AGB von Alloy
            </Link>{' '}
            einverstanden und habe die{' '}
            <Link title="AGB" href={RouteList.PrivacyPolicy} sx={{ color: 'grey.800' }} target="_blank">
              Datenschutzbestimmungen
            </Link>{' '}
            zur Kenntnis genommen.
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
